import { AddressFormat, DefaultTenantConfig, ITenantConfig } from '../domains/tenant-config';
import { getCities, getConfig, getCountries, getRegions } from '../api/tenant';
import UrlParser from '../utils/url-parser';
import { RootStore } from './root.store';
import { makeAutoObservable, runInAction } from 'mobx';
import { ICountry, IRegion } from '../domains/tenant.domain';
import { ErrorCode } from '../domains/errorCode';
import { getLanguageCode } from '../translations/i18n';

export class TenantStore {
  root: RootStore;

  private _config: ITenantConfig = new DefaultTenantConfig();
  private _countries: ICountry[] = [];
  private _regionsMap: Map<string, IRegion[]> = new Map<string, IRegion[]>();
  private _citiesMap: Map<string, string[]> = new Map<string, string[]>();

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  async init(): Promise<void> {
    try {
      const tenantId = UrlParser.getTenantId();
      if (!tenantId) return;
      const config = await getConfig(tenantId);
      await runInAction(async () => {
        this._config = { ...this._config, ...config };
        await this.root.signUpStore.changeLanguage(this.config?.language);
      });
    } catch {
      this.root.signUpStore.setErrorCode(ErrorCode.Undefined);
    }
  }

  get config(): ITenantConfig {
    return this._config;
  }

  get countries(): ICountry[] {
    return this._countries;
  }

  async setCountries(language: string) {
    const tenantId = UrlParser.getTenantId();
    if (!tenantId) return;
    const countries = await getCountries(tenantId, language);
    if (this._config.addressFormat !== AddressFormat.countries) {
      await Promise.all(
        countries.map(async (country: ICountry) => {
          const tenantConfig = country.tenantId === tenantId ? this.config : await getConfig(country.tenantId);
          country.minZipLength = tenantConfig.minZipLength;
          country.maxZipLength = tenantConfig.maxZipLength;
          country.isZipDigitsOnly = tenantConfig.isZipDigitsOnly;
        }),
      );
    }
    this._countries = countries;
  }

  async getRegions(countryCode: string): Promise<IRegion[]> {
    if (!this._countries.some(c => c.code === countryCode)) return [];
    const language = getLanguageCode();
    const key = `${countryCode}${language}`;
    if (!this._regionsMap.has(key)) this._regionsMap.set(key, await getRegions(UrlParser.getTenantId(), countryCode, language));
    return this._regionsMap.get(key) ?? [];
  }

  async getCities(countryCode: string, regionCode: string): Promise<string[]> {
    const key = `${countryCode}-${regionCode}-cities`;
    if (!this._citiesMap.has(key)) this._citiesMap.set(key, await getCities(countryCode, regionCode));
    return this._citiesMap.get(key) ?? [];
  }
}
